import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PieChart as RechartsPie,
  Pie,
  Cell,
  LineChart as RechartsLine,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart as RechartsBar,
  Bar,
  ResponsiveContainer,
} from 'recharts';
import { adminCriticalityApi, settingsApi } from '../services/api';
import {
  getCriticalityLabels,
  getLevelLabels,
  getCriticalityRowColors,
  getMatrixCellBg,
  getMatrixCellText,
} from '../utils/settingsScales';
import Card from './common/Card';

// Impact (row) × Automation (col) → Criticality (for cell color)
// Must match backend apply_criticality_matrix(): (Impact, Automation) → OES Criticality
const CRITICALITY_MATRIX = {
  MA: { MB: 'B', B: 'M', M: 'A', A: 'MA', MA: 'MA' },
  A:  { MB: 'B', B: 'M', M: 'A', A: 'A',  MA: 'A' },
  M:  { MB: 'MB', B: 'B', M: 'M', A: 'M',  MA: 'M' },
  B:  { MB: 'MB', B: 'B', M: 'B', A: 'B',  MA: 'B' },
  MB: { MB: 'MB', B: 'MB', M: 'MB', A: 'MB', MA: 'MB' },
};

const IMPACT_ROWS = ['MA', 'A', 'M', 'B', 'MB'];
const AUTOMATION_COLS = ['MB', 'B', 'M', 'A', 'MA'];

function OrderToggleButton({ descending, onClick, ariaLabel }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className="p-1 rounded text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors"
    >
      {descending ? (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
        </svg>
      ) : (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
      )}
    </button>
  );
}

// All default IT/OT categories from the assessment wizard (excluding Other)
const IT_OT_DEFAULT_CATEGORIES = [
  'SCADA', 'DCS', 'PLC', 'HMI', 'RTU', 'IT Infrastructure', 'Information Systems',
  '5G Systems', 'Core Banking', 'ERP', 'CRM', 'DBMS', 'Cloud Services',
  'Radar Systems', 'ATC (Air Traffic Control)', 'Power Grid Systems',
  'Water Management Systems', 'Transportation Systems', 'Medical Devices',
  'IoT Devices', 'Industrial Control Systems',
];

// Globe-like layout: chosen items scattered inside the circle; unchosen (0 frequency) in a small inner circle
const GLOBE_RADIUS_PERCENT = 42; // max radius for chosen items
const INNER_RADIUS_PERCENT = 14; // small circle for unchosen items (compact, low visibility)

const GOLDEN_ANGLE = Math.PI * 2 * (1 - 1 / ((1 + Math.sqrt(5)) / 2) ** 2); // ~137.5°

function getCirclePosition(index, totalCount, isChosen) {
  if (totalCount <= 0) return { left: '50%', top: '50%' };
  // Same sunflower fill for both: radius grows with index so points fill the disk from center to edge
  const t = (index + 1) / totalCount;
  const maxRadius = isChosen ? GLOBE_RADIUS_PERCENT : INNER_RADIUS_PERCENT;
  const r = maxRadius * Math.sqrt(t);
  const angle = index * GOLDEN_ANGLE - Math.PI / 2;
  const x = 50 + r * Math.cos(angle);
  const y = 50 + r * Math.sin(angle);
  return {
    left: `${Math.max(2, Math.min(98, x))}%`,
    top: `${Math.max(2, Math.min(98, y))}%`,
  };
}

export default function AdminCriticalityDashboard() {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [scales, setScales] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        setLoading(true);
        const response = await adminCriticalityApi.getDashboardMetrics();
        setMetrics(response.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load metrics');
      } finally {
        setLoading(false);
      }
    };
    fetchMetrics();
  }, []);

  useEffect(() => {
    settingsApi.getForCurrentUser()
      .then((r) => setScales(r.data?.scales ?? {}))
      .catch(() => setScales({}));
  }, []);

  const criticalityLabels = getCriticalityLabels(scales);
  const levelLabels = getLevelLabels(scales);
  const criticalityRowColors = getCriticalityRowColors(scales);
  const matrixCellBg = getMatrixCellBg(scales);
  const matrixCellText = getMatrixCellText(scales);
  const levelNumToLabel = useMemo(
    () => ({ 1: levelLabels.mb, 2: levelLabels.b, 3: levelLabels.m, 4: levelLabels.a, 5: levelLabels.ma }),
    [levelLabels]
  );

  const {
    most_critical_oeses = [],
    impact_ranking = [],
    automation_ranking = [],
    service_impact_ranking = [],
    process_automation_ranking = [],
    it_ot_category_counts = {},
    matrix_oeses = [],
    assessment_status_counts = { draft: 0, active: 0, paused: 0, completed: 0, archived: 0 },
    assessments_over_time = [],
    criticality_over_time = [],
  } = metrics || {};
  const maxCount = Math.max(...Object.values(it_ot_category_counts), 1);

  // Chart data: assessment status (doughnut)
  const statusChartData = useMemo(() => {
    const labels = { draft: 'Draft', active: 'Active', paused: 'Paused', completed: 'Completed', archived: 'Archived' };
    const colors = ['#94a3b8', '#29ABE2', '#f59e0b', '#10b981', '#64748b'];
    return Object.entries(assessment_status_counts)
      .filter(([, v]) => v > 0)
      .map(([key], i) => ({ id: key, value: assessment_status_counts[key], label: labels[key] || key, color: colors[i % colors.length] }));
  }, [assessment_status_counts]);

  // Chart data: criticality distribution (doughnut) from most_critical_oeses
  const criticalityChartData = useMemo(() => {
    const counts = { ma: 0, a: 0, m: 0, b: 0, mb: 0 };
    most_critical_oeses.forEach((o) => {
      const k = (o.oes_criticality || '').toString().toLowerCase();
      if (counts[k] !== undefined) counts[k]++;
    });
    return Object.entries(counts)
      .filter(([, v]) => v > 0)
      .map(([key]) => ({
        id: key,
        value: counts[key],
        label: criticalityLabels[key] || key,
        color: { ma: '#dc2626', a: '#f97316', m: '#fbbf24', b: '#facc15', mb: '#fde68a' }[key] || '#94a3b8',
      }));
  }, [most_critical_oeses, criticalityLabels]);

  // Criticality level over time: one line per OES, x = assessment_date, y = level_numeric (1–5)
  const criticalityOverTimeSeries = useMemo(() => {
    if (!criticality_over_time.length) return [];
    const seen = new Map();
    criticality_over_time.forEach(({ oes_id, oes_name }) => {
      if (!seen.has(oes_id)) seen.set(oes_id, { key: `oes_${oes_id}`, name: (oes_name || `OES ${oes_id}`).slice(0, 24) });
    });
    return Array.from(seen.values());
  }, [criticality_over_time]);

  const criticalityOverTimeChartData = useMemo(() => {
    if (!criticality_over_time.length) return [];
    const dateToRow = {};
    criticality_over_time.forEach(({ oes_id, assessment_date, level_numeric }) => {
      const dateStr = (assessment_date || '').slice(0, 10);
      if (!dateStr) return;
      const key = `oes_${oes_id}`;
      if (!dateToRow[dateStr]) dateToRow[dateStr] = { date: dateStr };
      dateToRow[dateStr][key] = level_numeric;
    });
    return Object.keys(dateToRow)
      .sort()
      .map((date) => dateToRow[date]);
  }, [criticality_over_time]);

  // Distinct colors for up to ~8 OES lines (EU blue family + heatmap-style)
  const CRITICALITY_LINE_COLORS = ['#003399', '#29ABE2', '#dc2626', '#f97316', '#eab308', '#10b981', '#6366f1', '#8b5cf6'];

  // Horizontal bar: top 5 OESes by criticality (use impact_ranking for “impact score” or most_critical count)
  const criticalityLevelNum = { ma: 5, a: 4, m: 3, b: 2, mb: 1 };
  const topOesBarSeries = useMemo(() => {
    const top = most_critical_oeses.slice(0, 6);
    return top.map((o) => ({
      name: (o.oes_name || 'OES').slice(0, 18),
      value: criticalityLevelNum[(o.oes_criticality || '').toString().toLowerCase()] ?? 0,
    }));
  }, [most_critical_oeses]);

  const [mostCriticalDesc, setMostCriticalDesc] = useState(true);
  const [impactDesc, setImpactDesc] = useState(true);
  const [automationDesc, setAutomationDesc] = useState(true);
  const [serviceImpactDesc, setServiceImpactDesc] = useState(true);
  const [processAutomationDesc, setProcessAutomationDesc] = useState(true);

  const mostCriticalOrdered = mostCriticalDesc ? most_critical_oeses : [...most_critical_oeses].reverse();
  const impactOrdered = impactDesc ? impact_ranking : [...impact_ranking].reverse();
  const automationOrdered = automationDesc ? automation_ranking : [...automation_ranking].reverse();
  const serviceImpactOrdered = serviceImpactDesc ? service_impact_ranking : [...service_impact_ranking].reverse();
  const processAutomationOrdered = processAutomationDesc ? process_automation_ranking : [...process_automation_ranking].reverse();

  const scoreToLevelKey = (score) => {
    if (score >= 4.5) return 'ma';
    if (score >= 3.5) return 'a';
    if (score >= 2.5) return 'm';
    if (score >= 1.5) return 'b';
    return 'mb';
  };

  const matrixOesByCell = useMemo(() => {
    const map = {};
    IMPACT_ROWS.forEach((r) => { map[r] = {}; AUTOMATION_COLS.forEach((c) => { map[r][c] = []; }); });
    matrix_oeses.forEach((item) => {
      const r = (item.impact_level || '').toUpperCase();
      const c = (item.automation_level || '').toUpperCase();
      if (map[r] && map[r][c]) map[r][c].push(item);
    });
    return map;
  }, [matrix_oeses]);

  const itOtItems = useMemo(() => {
    const categories = IT_OT_DEFAULT_CATEGORIES;
    const chosen = categories.filter((name) => (it_ot_category_counts[name] ?? 0) > 0);
    const unchosen = categories.filter((name) => (it_ot_category_counts[name] ?? 0) === 0);
    const chosenIndexBy = new Map(chosen.map((name, i) => [name, { index: i, total: chosen.length }]));
    const unchosenIndexBy = new Map(unchosen.map((name, i) => [name, { index: i, total: unchosen.length }]));

    return categories.map((name) => {
      const count = it_ot_category_counts[name] ?? 0;
      const isChosen = count > 0;
      const { index, total } = isChosen ? chosenIndexBy.get(name) : unchosenIndexBy.get(name);
      const pos = getCirclePosition(index, total, isChosen);
      const ratio = maxCount > 0 ? count / maxCount : 0;
      const fontSize = count === 0 ? 11 : Math.max(10, Math.min(18, Math.round(10 + ratio * 8)));
      return { name, count, ...pos, fontSize, chosen: isChosen };
    });
  }, [it_ot_category_counts, maxCount]);

  if (loading) {
    return (
      <Card>
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-200 border-t-brand-500 mx-auto" />
          <p className="mt-4 text-slate-600 text-sm font-medium">Loading criticality metrics...</p>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <div className="text-red-600 text-sm">{error}</div>
      </Card>
    );
  }

  const FADE_DURATION_MS = 450;
  const STAGGER_MS = 90;
  const widgetStyle = (index) => ({
    animation: `dashboardFadeIn ${FADE_DURATION_MS}ms ease-out both`,
    animationDelay: `${index * STAGGER_MS}ms`,
  });

  return (
    <div className="space-y-6">
      <style>{`
        @keyframes dashboardFadeIn {
          from { opacity: 0; transform: translateY(14px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        <div className="space-y-4 pr-1">
          <Card className="p-5" style={widgetStyle(1)}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Most Critical OESes</h3>
              <OrderToggleButton
                descending={mostCriticalDesc}
                onClick={() => setMostCriticalDesc((d) => !d)}
                ariaLabel={mostCriticalDesc ? 'Descending order (click for ascending)' : 'Ascending order (click for descending)'}
              />
            </div>
            {most_critical_oeses.length === 0 ? (
              <p className="text-slate-400 text-xs text-center py-3">No completed assessments yet.</p>
            ) : (
              <ul className="space-y-1">
                {mostCriticalOrdered.map((item, idx) => {
                  const key = item.oes_criticality?.toLowerCase() || 'm';
                  const dotColor = { ma: 'bg-red-500', a: 'bg-orange-400', m: 'bg-amber-400', b: 'bg-blue-400', mb: 'bg-emerald-400' }[key] || 'bg-slate-300';
                  const badgeBg = { ma: 'bg-red-50 text-red-700', a: 'bg-orange-50 text-orange-700', m: 'bg-amber-50 text-amber-700', b: 'bg-blue-50 text-blue-700', mb: 'bg-emerald-50 text-emerald-700' }[key] || 'bg-slate-50 text-slate-600';
                  return (
                    <li
                      key={item.id}
                      className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-slate-50 transition-colors group"
                    >
                      <span className="text-[10px] font-mono text-slate-300 w-4 text-right">{idx + 1}</span>
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dotColor}`} />
                      <span className="text-xs font-medium text-slate-700 truncate flex-1">{item.oes_name}</span>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${badgeBg}`}>
                        {criticalityLabels[key] || item.oes_criticality}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>

          <Card className="p-5" style={widgetStyle(2)}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Impact</h3>
              <OrderToggleButton
                descending={impactDesc}
                onClick={() => setImpactDesc((d) => !d)}
                ariaLabel={impactDesc ? 'High to low (click for low to high)' : 'Low to high (click for high to low)'}
              />
            </div>
            {impact_ranking.length === 0 ? (
              <p className="text-slate-400 text-xs text-center py-3">No data yet.</p>
            ) : (
              <ul className="space-y-0.5">
                {impactOrdered.map((item) => {
                  const key = item.impact_level?.toLowerCase() || 'm';
                  const barColor = { ma: '#ef4444', a: '#f97316', m: '#eab308', b: '#3b82f6', mb: '#22c55e' }[key] || '#94a3b8';
                  const levelNum = { ma: 5, a: 4, m: 3, b: 2, mb: 1 }[key] || 0;
                  return (
                    <li key={item.oes_id} className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg hover:bg-slate-50 transition-colors">
                      <span className="text-xs font-medium text-slate-600 truncate w-28 text-right">{item.oes_name}</span>
                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${levelNum * 20}%`, backgroundColor: barColor }} />
                      </div>
                      <span className="text-[10px] font-semibold text-slate-500 w-16 text-right">{levelLabels[key] || item.impact_level}</span>
                    </li>
                  );
                })}
              </ul>
            )}
            {service_impact_ranking.length > 0 && (
              <>
                <div className="flex items-center justify-between mt-4 mb-2 pt-3 border-t border-slate-100">
                  <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">By Service</span>
                  <OrderToggleButton
                    descending={serviceImpactDesc}
                    onClick={() => setServiceImpactDesc((d) => !d)}
                    ariaLabel={serviceImpactDesc ? 'High to low (click for low to high)' : 'Low to high (click for high to low)'}
                  />
                </div>
                <ul className="space-y-0.5">
                  {serviceImpactOrdered.map((item, idx) => {
                    const key = scoreToLevelKey(item.service_impact_avg ?? 0);
                    const barColor = { ma: '#ef4444', a: '#f97316', m: '#eab308', b: '#3b82f6', mb: '#22c55e' }[key] || '#94a3b8';
                    const serviceName = (item.service_name ?? item.serviceName ?? '').trim() || 'Service';
                    const oesName = item.oes_name ?? item.oesName ?? '';
                    const score = item.service_impact_avg ?? 0;
                    return (
                      <li
                        key={`service-${item.oes_id}-${idx}-${serviceName.slice(0, 20)}`}
                        className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg hover:bg-slate-50 transition-colors"
                        title={`${serviceName} — ${oesName}`}
                      >
                        <div className="w-28 min-w-0 text-right">
                          <div className="text-xs font-medium text-slate-600 truncate">{serviceName}</div>
                          <div className="text-[9px] text-slate-400 truncate">{oesName}</div>
                        </div>
                        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${(score / 5) * 100}%`, backgroundColor: barColor }} />
                        </div>
                        <span className="text-[10px] font-bold text-slate-400 tabular-nums w-7 text-right">{score.toFixed(1)}</span>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </Card>

          <Card className="p-5" style={widgetStyle(3)}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Automation</h3>
              <OrderToggleButton
                descending={automationDesc}
                onClick={() => setAutomationDesc((d) => !d)}
                ariaLabel={automationDesc ? 'High to low (click for low to high)' : 'Low to high (click for high to low)'}
              />
            </div>
            {automation_ranking.length === 0 ? (
              <p className="text-slate-400 text-xs text-center py-3">No data yet.</p>
            ) : (
              <ul className="space-y-0.5">
                {automationOrdered.map((item) => {
                  const key = item.automation_level?.toLowerCase() || 'm';
                  const barColor = { ma: '#ef4444', a: '#f97316', m: '#eab308', b: '#3b82f6', mb: '#22c55e' }[key] || '#94a3b8';
                  const levelNum = { ma: 5, a: 4, m: 3, b: 2, mb: 1 }[key] || 0;
                  return (
                    <li key={item.oes_id} className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg hover:bg-slate-50 transition-colors">
                      <span className="text-xs font-medium text-slate-600 truncate w-28 text-right">{item.oes_name}</span>
                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${levelNum * 20}%`, backgroundColor: barColor }} />
                      </div>
                      <span className="text-[10px] font-semibold text-slate-500 w-16 text-right">{levelLabels[key] || item.automation_level}</span>
                    </li>
                  );
                })}
              </ul>
            )}
            {process_automation_ranking.length > 0 && (
              <>
                <div className="flex items-center justify-between mt-4 mb-2 pt-3 border-t border-slate-100">
                  <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">By Process</span>
                  <OrderToggleButton
                    descending={processAutomationDesc}
                    onClick={() => setProcessAutomationDesc((d) => !d)}
                    ariaLabel={processAutomationDesc ? 'High to low (click for low to high)' : 'Low to high (click for high to low)'}
                  />
                </div>
                <ul className="space-y-0.5">
                  {processAutomationOrdered.map((item, idx) => {
                    const key = scoreToLevelKey(item.process_automation_avg ?? 0);
                    const barColor = { ma: '#ef4444', a: '#f97316', m: '#eab308', b: '#3b82f6', mb: '#22c55e' }[key] || '#94a3b8';
                    const processName = (item.process_name ?? item.processName ?? '').trim() || 'Process';
                    const oesName = item.oes_name ?? item.oesName ?? '';
                    const score = item.process_automation_avg ?? 0;
                    return (
                      <li
                        key={`process-${item.oes_id}-${idx}-${processName.slice(0, 20)}`}
                        className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg hover:bg-slate-50 transition-colors"
                        title={`${processName} — ${oesName}`}
                      >
                        <div className="w-28 min-w-0 text-right">
                          <div className="text-xs font-medium text-slate-600 truncate">{processName}</div>
                          <div className="text-[9px] text-slate-400 truncate">{oesName}</div>
                        </div>
                        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${(score / 5) * 100}%`, backgroundColor: barColor }} />
                        </div>
                        <span className="text-[10px] font-bold text-slate-400 tabular-nums w-7 text-right">{score.toFixed(1)}</span>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </Card>
        </div>

        {/* Charts section (replaces matrix in this slot) */}
        <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4 min-w-0" style={widgetStyle(4)}>
          <Card className="p-5 overflow-visible">
            <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wide mb-3">Assessments by status</h3>
            {statusChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <RechartsPie>
                  <Pie
                    data={statusChartData}
                    dataKey="value"
                    nameKey="label"
                    cx="50%"
                    cy="50%"
                    innerRadius={54}
                    outerRadius={82}
                    paddingAngle={3}
                    strokeWidth={0}
                    label={({ payload, cx, x, y }) => (
                      <text x={x} y={y} textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fill="#475569" fontSize={11} fontWeight={500}>
                        {`${payload.label}: ${payload.value}`}
                      </text>
                    )}
                  >
                    {statusChartData.map((entry, i) => (
                      <Cell key={entry.id} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name) => [value, name]} contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }} />
                </RechartsPie>
              </ResponsiveContainer>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-slate-500 text-sm">No assessment data</div>
            )}
          </Card>
          <Card className="p-5 overflow-visible">
            <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wide mb-3">OES criticality distribution</h3>
            {criticalityChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <RechartsPie>
                  <Pie
                    data={criticalityChartData}
                    dataKey="value"
                    nameKey="label"
                    cx="50%"
                    cy="50%"
                    innerRadius={54}
                    outerRadius={82}
                    paddingAngle={3}
                    strokeWidth={0}
                    label={({ payload, cx, x, y }) => (
                      <text x={x} y={y} textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fill="#475569" fontSize={11} fontWeight={500}>
                        {`${payload.label}: ${payload.value}`}
                      </text>
                    )}
                  >
                    {criticalityChartData.map((entry) => (
                      <Cell key={entry.id} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name) => [value, name]} contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }} />
                </RechartsPie>
              </ResponsiveContainer>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-slate-500 text-sm">No OES data yet</div>
            )}
          </Card>
          <Card className="p-5 overflow-hidden sm:col-span-2">
            <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wide mb-3">Criticality level over time</h3>
            {criticalityOverTimeChartData.length > 0 && criticalityOverTimeSeries.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <RechartsLine data={criticalityOverTimeChartData} margin={{ top: 12, right: 24, bottom: 36, left: 36 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} />
                  <YAxis domain={[1, 5]} ticks={[1, 2, 3, 4, 5]} tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={(v) => levelNumToLabel[v] || v} />
                  <Tooltip
                    formatter={(value, name) => (value != null ? [`${value} — ${levelNumToLabel[value] || ''}`, name] : [null, name])}
                    labelFormatter={(label) => `Date: ${label}`}
                    contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.07)' }}
                  />
                  {criticalityOverTimeSeries.map((s, i) => (
                    <Line
                      key={s.key}
                      type="monotone"
                      dataKey={s.key}
                      name={s.name}
                      stroke={CRITICALITY_LINE_COLORS[i % CRITICALITY_LINE_COLORS.length]}
                      strokeWidth={2.5}
                      dot={{ fill: '#fff', stroke: CRITICALITY_LINE_COLORS[i % CRITICALITY_LINE_COLORS.length], strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, strokeWidth: 2 }}
                      connectNulls
                    />
                  ))}
                </RechartsLine>
              </ResponsiveContainer>
            ) : (
              <div className="h-[220px] flex items-center justify-center text-slate-500 text-sm">Complete assessments over time to see criticality trends per OES</div>
            )}
          </Card>
          <Card className="p-5 overflow-hidden sm:col-span-2">
            <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wide mb-3">Most critical OESes (top 6)</h3>
            {topOesBarSeries.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <RechartsBar
                  data={topOesBarSeries}
                  layout="vertical"
                  margin={{ top: 8, right: 24, bottom: 8, left: 80 }}
                  barCategoryGap={12}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                  <XAxis type="number" domain={[0, 5]} tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} />
                  <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.07)' }} />
                  <Bar dataKey="value" name="Criticality level" fill="#6366f1" radius={[0, 6, 6, 0]} barSize={14}>
                    {topOesBarSeries.map((entry, i) => {
                      const colors = { 5: '#dc2626', 4: '#f97316', 3: '#eab308', 2: '#3b82f6', 1: '#22c55e' };
                      return <Cell key={i} fill={colors[entry.value] || '#94a3b8'} />;
                    })}
                  </Bar>
                </RechartsBar>
              </ResponsiveContainer>
            ) : (
              <div className="h-[220px] flex items-center justify-center text-slate-500 text-sm">No OES data yet</div>
            )}
          </Card>
        </div>
      </div>

      {/* Row: IT/OT (half) + Criticality matrix (half) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="overflow-hidden flex flex-col" style={widgetStyle(5)}>
          <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wide mb-1">IT/OT technology categories</h3>
          <p className="text-xs text-slate-500 mb-3">All categories from the assessment; size reflects how often they were chosen.</p>
          <div
            className="relative w-full rounded-2xl bg-slate-50 border border-slate-200/60"
            style={{ minHeight: 360 }}
          >
            {itOtItems.map(({ name, count, left, top, fontSize, chosen }) => (
              <span
                key={name}
                className="absolute transform -translate-x-1/2 -translate-y-1/2 whitespace-nowrap select-none transition-all duration-300"
                style={{
                  left,
                  top,
                  zIndex: chosen ? 10 : 1,
                  fontSize: `${fontSize}px`,
                  fontWeight: chosen ? 600 : 400,
                  color: chosen ? 'rgb(67 56 202)' : 'rgb(107 114 128)',
                  opacity: chosen ? 1 : 0.65,
                  textShadow: chosen ? '0 0 20px rgba(59, 130, 246, 0.15)' : 'none',
                  filter: chosen ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.08))' : 'none',
                }}
                title={count > 0 ? `Chosen ${count} time(s)` : 'Chosen 0 times'}
              >
                <span
                  className={`
                    inline-block px-2.5 py-0.5 rounded-full
                    ${chosen
                      ? 'bg-brand-100/90 text-brand-600 ring-1 ring-brand-200/60 hover:bg-brand-200/80 hover:ring-brand-300/70 shadow-sm'
                      : 'bg-slate-100/70 text-slate-500 hover:bg-slate-200/80 hover:text-slate-600'
                    }
                  `}
                >
                  {name}
                  {count > 0 && <span className="ml-1.5 text-inherit opacity-80 font-medium">({count})</span>}
                </span>
              </span>
            ))}
          </div>
        </Card>

        <Card className="p-6 overflow-hidden flex flex-col min-w-0" style={widgetStyle(6)}>
          <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4">Criticality Matrix — OESes by Quadrant</h3>
          <div className="w-full min-w-0 flex-1 rounded-xl overflow-hidden bg-white">
            <div className="flex items-stretch w-full">
              <div className="flex items-center justify-center py-4 pr-1.5" style={{ minWidth: 24 }}>
                <span
                  className="text-[9px] font-semibold uppercase tracking-widest text-slate-400 whitespace-nowrap"
                  style={{ writingMode: 'vertical-rl', transform: 'rotate(-180deg)' }}
                >
                  Impact
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="grid grid-cols-6" style={{ gridAutoRows: 'minmax(60px, auto)', gap: '1px', background: '#e2e8f0' }}>
                  <div className="col-span-1 row-span-1 flex items-center justify-center bg-slate-50 p-2">
                    <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400"></span>
                  </div>
                  {AUTOMATION_COLS.map((col) => (
                    <div
                      key={col}
                      className="flex items-center justify-center bg-slate-50 p-2"
                    >
                      <span className="text-[10px] font-semibold text-slate-500">{levelLabels[col.toLowerCase()]}</span>
                    </div>
                  ))}
                  {IMPACT_ROWS.map((row) => (
                    <React.Fragment key={row}>
                      <div className="flex items-center justify-center bg-slate-50 p-2">
                        <span className="text-[10px] font-semibold text-slate-500">{levelLabels[row.toLowerCase()]}</span>
                      </div>
                      {AUTOMATION_COLS.map((col) => {
                        const crit = CRITICALITY_MATRIX[row]?.[col] || '';
                        const critKey = crit?.toLowerCase() || '';
                        const oesList = matrixOesByCell[row]?.[col] || [];
                        // Softer cell colors
                        const cellBg = { ma: '#fca5a5', a: '#fdba74', m: '#fcd34d', b: '#93c5fd', mb: '#86efac' }[critKey] || '#f1f5f9';
                        const cellBorder = { ma: '#ef4444', a: '#f97316', m: '#eab308', b: '#3b82f6', mb: '#22c55e' }[critKey] || '#cbd5e1';
                        return (
                          <div
                            key={`${row}-${col}`}
                            className="min-h-[60px] p-2 flex flex-wrap items-center justify-center gap-1.5"
                            style={{ backgroundColor: cellBg }}
                          >
                            {oesList.length === 0 ? (
                              <span className="text-[10px] text-slate-300">—</span>
                            ) : (
                              oesList.map((oes) => (
                                <div
                                  key={oes.oes_id}
                                  className="relative w-7 h-7 flex-shrink-0 rounded-md overflow-hidden"
                                  style={{ backgroundColor: cellBorder, border: `1.5px solid ${cellBorder}` }}
                                  title={oes.oes_name}
                                >
                                  <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-slate-600 pointer-events-none">
                                    {(oes.oes_name || '?').slice(0, 1).toUpperCase()}
                                  </span>
                                  {oes.oes_logo && (
                                    <img
                                      src={oes.oes_logo}
                                      alt=""
                                      className="absolute inset-0 w-full h-full object-contain"
                                      onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                  )}
                                </div>
                              ))
                            )}
                          </div>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </div>
                <div className="flex justify-center py-2">
                  <span className="text-[9px] font-semibold uppercase tracking-widest text-slate-400">Automation</span>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
